import Node from 'nanogl-node'

import { RunnerActor } from '@/state/RunnerStateMachine'
import { RenderContext } from '@/webgl/core/RenderContext'
import DebugDraw from '@/webgl/dev/debugDraw/DebugDraw'
import { IScene } from '@/webgl/engine/IScene'
import Renderer from '@/webgl/Renderer'
import GameWorld from '@/webgl/runner/GameWorld'
import { IRunnerRenderer } from '@/webgl/runner/interfaces/IRunnerRenderer'
import RunnerCamera from '@/webgl/runner/RunnerCamera'
import ScenarioAudio from '@/webgl/runner/scenarios/ScenarioAudio'
import RunnerRenderer from '@/webgl/runner/SceneRenderer'
import PostprocessSystem from '@/webgl/runner/systems/PostprocessSystem'
import SequencingSystem from '@/webgl/runner/systems/SequencingSystem'

import { AudioSegment } from '../segments/AudioSegment'
import Countdown from '../segments/Countdown'
import { Intro } from '../segments/Intro'
import { Outro } from '../segments/Outro'
import { PlaySegment } from '../segments/PlaySegment'
import { Popin } from '../segments/Popin'
import SpiderSegment from '../segments/SpiderSegment'
import Tooltip from '../segments/Tooltip'
import { TransitionSegment } from '../segments/TransitionSegment'
import SETUP from './Francesca1Setup'

export default class Francesca1Scene implements IScene {
  glRenderer: Renderer
  root: Node
  camCtrl: RunnerCamera
  world: GameWorld
  renderer: IRunnerRenderer
  audio: ScenarioAudio

  constructor(renderer: Renderer) {
    this.glRenderer = renderer

    this.root = new Node()
    this.world = new GameWorld(this.root)
    this.camCtrl = new RunnerCamera(this.world)
    this.renderer = new RunnerRenderer(renderer, this.world, SETUP)

    this.world.getTrackingSystem().useDomElement(this.glRenderer.ilayer)

    this.world.registry.init(SETUP)
    this.world.systems.add(new PostprocessSystem(this.world, this.glRenderer.postprocess))
    this.world.background.spawnableItems = SETUP.sequences[0].scrolling

    this.audio = new ScenarioAudio('en', SETUP.audio, SETUP.audioSfx)

    const sequencing = new SequencingSystem(this.world)
    this.world.systems.add(sequencing)

    sequencing
      .init(this.renderer, SETUP)
      .add(new Intro('introduction'))
      .add(new AudioSegment('audio_intro'))
      .add(new Popin('popin1', true))
      .add(new AudioSegment('audio_teacher'))
      .add(new Countdown('countdown'))
      .add(new Tooltip('move'))
      .add(new Tooltip('pause'))
      .add(new Tooltip('stick'))
      .add(new PlaySegment('equation_1_a'))
      .add(new PlaySegment('equation_1_b'))
      .add(new PlaySegment('equation_1_visual'))
      .add(new TransitionSegment('transition_equation_1'))
      .add(new Popin('popin2'))
      .add(new PlaySegment('equation_2_a'))
      .add(new PlaySegment('equation_2_switch_a'))
      .add(new PlaySegment('equation_2_switch_b'))
      .add(new TransitionSegment('transition_equation_2'))
      .add(new Popin('popin3'))
      .add(new PlaySegment('equation_3_helmet_a'))
      .add(new PlaySegment('equation_3_helmet_b'))
      .add(new PlaySegment('equation_3_spider_1'))
      .add(new PlaySegment('equation_3_spider_2'))
      .add(new SpiderSegment('spider'))
      .add(new AudioSegment('audio_spider'))
      .add(new Outro('outro'))

    /// #if _DEBUG
    const params = new URLSearchParams(window.location.search)
    if (params.get('segment')) {
      sequencing.setStartSegmentId(params.get('segment'))
    }
    /// #endif
  }

  public setActor(actor: RunnerActor) {
    this.world.state.setActor(actor)
  }

  async load(abort: AbortSignal): Promise<void> {
    await Promise.all([
      this.renderer.load(abort),
      this.world.systems.get<SequencingSystem>(SequencingSystem).load(abort),
      this.audio.load(abort),
    ])
  }

  start() {
    this.glRenderer.cameras.getManager('main').setControler(this.camCtrl)

    this.world.state.setJauge(0)
    this.world.start()
  }

  preRender(): void {
    this.world.update(this.glRenderer.dt)
    const time = this.world.time
    this.renderer.preRender(time)
    this.root.updateWorldMatrix()
  }

  rttPass(context: RenderContext): void {
    this.renderer.rttPass(context)
  }

  render(context: RenderContext): void {
    this.renderer.render(context)
    this.world.systems.forEach((system) => system.render?.(context))

    if (this.camCtrl.camera) DebugDraw.drawFrustum(this.camCtrl.camera._viewProj)
  }

  dispose(): void {
    this.world.dispose()
    this.audio.dispose()
  }
}
